import dayjs, { type ConfigType, type Dayjs } from 'dayjs';
import moment from 'moment-timezone';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import localeData from 'dayjs/plugin/localeData';
import relativeTime from 'dayjs/plugin/relativeTime';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration from 'dayjs/plugin/duration';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';

import CompanyInfoVO from '@interfaces/CompanyInfoVO';

dayjs.extend(customParseFormat);
dayjs.extend(relativeTime);
dayjs.extend(localeData);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(localizedFormat);
dayjs.extend(duration);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

let defaultTimeZone: string | null = null;
let defaultDateFormat: string | null = null;
let defaultTimeFormat: string | null = null;
const isSafari = navigator.userAgent.indexOf('Chrome') < 0 && navigator.userAgent.indexOf('Safari') >= 0;
const isFireFox = navigator.userAgent.indexOf('Firefox') > 0;

export const setCompanyFormats = (companyInfoVO: CompanyInfoVO) => {
  if (companyInfoVO.uiPreferences && companyInfoVO.uiPreferences.dateFormat && companyInfoVO.uiPreferences.timeFormat && companyInfoVO.timeZone) {
    defaultDateFormat = companyInfoVO.uiPreferences.dateFormat;
    defaultTimeFormat = companyInfoVO.uiPreferences.timeFormat;
    defaultTimeZone = companyInfoVO.timeZone;
  } else {
    defaultDateFormat = 'YYYY-MM-DD';
    defaultTimeFormat = 'h:mm:ss a';
    defaultTimeZone = 'America/New_York';
  }
};

const parseDateWithTzOffset = (input: unknown, format: string, timeZone: string) => {
  // Anchor date-only strings to the company timezone so formatting does not drift by browser locale.
  const timezoneOffset = dayjs().tz(timeZone).format('Z');
  return dayjs(`${input} ${timezoneOffset}`, `${format} Z`);
};

export const getDayjsDateWithTimeZone = (inDate: unknown, timeZone?: string | null) => {
  if (!timeZone) {
    timeZone = defaultTimeZone;
  }
  const tz = timeZone as string;
  const dIn = inDate as ConfigType;

  if (isSafari || isFireFox) {
    return getMomentDateWithTimeZone(inDate, tz);
  }

  let tempDayjs: Dayjs;
  if (dayjs.isDayjs(inDate)) {
    // Normalize existing dayjs instances into the requested timezone instead of reparsing them.
    tempDayjs = (inDate as Dayjs).tz(tz);
  } else if (dayjs(dIn, 'YYYY-MM-DD', true).isValid()) {
    tempDayjs = parseDateWithTzOffset(inDate, 'YYYY-MM-DD', tz);
  } else if (dayjs(dIn, 'DD-MMM-YYYY', true).isValid()) {
    tempDayjs = parseDateWithTzOffset(inDate, 'DD-MMM-YYYY', tz);
  } else if (dayjs(dIn, 'YYYY-MM-DDTHH:mm:ss.000Z', true).isValid()) {
    tempDayjs = dayjs(dIn, 'YYYY-MM-DDTHH:mm:ss.000Z');
  } else if (dayjs(dIn, 'YYYY-MM-DD HH:mm:ss Z', true).isValid()) {
    tempDayjs = dayjs(dIn, 'YYYY-MM-DD HH:mm:ss Z');
  } else if (defaultDateFormat && dayjs(dIn, defaultDateFormat, true).isValid()) {
    // Respect the company-configured date format when the input is user-facing rather than ISO.
    tempDayjs = parseDateWithTzOffset(inDate, defaultDateFormat, tz);
  } else {
    const split = inDate && typeof inDate === 'string' ? inDate.split(' ') : inDate;
    if (split && (split as { length?: number }).length === 3) {
      // Preserve the legacy fallback that handles values shaped like "06-Jan-2024 10:00 +00:00".
      const parts = split as string[];
      parts[0] = parts[0].replaceAll('-', ' ');
      const modifiedDate = parts.join(' ');
      tempDayjs = dayjs(modifiedDate).isValid() ? dayjs(modifiedDate) : dayjs(dIn);
    } else {
      tempDayjs = dayjs(dIn);
    }
  }
  return tempDayjs.tz(tz);
};

export const getMomentDateWithTimeZone = (inDate: unknown, timeZone?: string | null, skipTimeZone: boolean = false) => {
  if (!timeZone) {
    timeZone = defaultTimeZone;
  }
  const tz = timeZone as string;
  let tempMoment;
  if (moment.isMoment(inDate)) {
    tempMoment = skipTimeZone ? moment.parseZone(inDate) : inDate;
  } else if (moment(inDate, 'YYYY-MM-DD', true).isValid()) {
    const newTz = skipTimeZone ? moment().format('Z') : moment().tz(tz).format('Z');
    tempMoment = moment(`${inDate} ${newTz}`, 'YYYY-MM-DD Z', true);
  } else if (moment(inDate, 'DD-MMM-YYYY', true).isValid()) {
    const newTz = skipTimeZone ? moment().format('Z') : moment().tz(tz).format('Z');
    tempMoment = moment(`${inDate} ${newTz}`, 'DD-MMM-YYYY Z', true);
  } else if (moment(inDate, 'YYYY-MM-DDTHH:mm:ss.000Z', true).isValid()) {
    tempMoment = moment(inDate, 'YYYY-MM-DDTHH:mm:ss.000Z', true);
  } else if (moment(inDate, 'YYYY-MM-DD HH:mm:ss Z', true).isValid()) {
    tempMoment = moment(inDate, 'YYYY-MM-DD HH:mm:ss Z', true);
  } else if (defaultDateFormat && moment(inDate, defaultDateFormat, true).isValid()) {
    const newTz = skipTimeZone ? moment().format('Z') : moment().tz(tz).format('Z');
    tempMoment = moment(`${inDate} ${newTz}`, `${defaultDateFormat} Z`, true);
  } else {
    tempMoment = moment(inDate);
  }
  return skipTimeZone ? tempMoment : tempMoment.tz(tz);
};

export const getFullDateTime = (displayDate: unknown, seperator?: string) => {
  const seperation = seperator || ' ';
  return getDayjsDateWithTimeZone(displayDate).format(defaultDateFormat + seperation + defaultTimeFormat);
};

export const getCurrentDate = () => {
  return dayjs();
};

const getDayjsDate = (inDate: unknown) => {
  return getDayjsDateWithTimeZone(inDate);
};

export const displayDateDifferences = (displayDate: string) => {
  const out: string = getDayjsDate(displayDate).fromNow(true);
  const currentDate = dayjs();
  if (out.includes('second')) {
    return 'Just now';
  }
  if (out.includes('minute')) {
    let num: string = out.split(' ')[0];
    if (num === 'a') {
      num = '1';
    }
    return `${num + (num !== '1' ? ' mins ' : ' min ')}ago`;
  }
  if (out.includes('hour')) {
    let num: string = out.split(' ')[0];
    if (num === 'an') {
      num = '1';
    }
    return `${num + (num !== '1' ? ' hours ' : ' hour ')}ago`;
  }
  const num: number = Math.ceil(currentDate.diff(displayDate, 'days', true));
  return `${num + (num !== 1 ? ' days ' : ' day ')}ago`;
};

export const getDateFormat = (inDate: unknown) => {
  return getDayjsDateWithTimeZone(inDate).format(defaultDateFormat as string);
};

export const getDateDifferenceinDays = (displayDate: unknown) => {
  return dayjs(displayDate as ConfigType).diff(dayjs(new Date()), 'day');
};

export const getShortDate = (inDate: unknown) => {
  return getDayjsDateWithTimeZone(inDate).format(defaultDateFormat as string);
};

export const converDateToBackendFormatByDateformat = (e: unknown, dateFormat: string) => {
  const d = e as ConfigType;
  const z = defaultTimeZone as string;

  // Parse known inbound formats explicitly before the generic fallback so backend payloads stay stable.
  if (dayjs(d, 'YYYY-MM-DD', true).isValid()) {
    const dateTz = dayjs(d, 'YYYY-MM-DD').tz(z).local().toDate();
    return dayjs(dateTz).format(dateFormat);
  }
  if (dayjs(d, 'DD-MMM-YYYY', true).isValid()) {
    const dateTz = dayjs(d, 'DD-MMM-YYYY').tz(z).local().toDate();
    return dayjs(dateTz).format(dateFormat);
  }
  if (dayjs(d, 'YYYY-MM-DDTHH:mm:ss.000Z', true).isValid()) {
    const dateTz = dayjs(d, 'YYYY-MM-DDTHH:mm:ss.000Z').tz(z).local().toDate();
    return dayjs(dateTz).format(dateFormat);
  }
  if (dayjs(d, 'YYYY-MM-DD HH:mm:ss Z').isValid()) {
    const dateTz = dayjs(d, 'YYYY-MM-DD HH:mm:ss Z').tz(z).local().toDate();
    return dayjs(dateTz).format(dateFormat);
  }
  if (dayjs(d, 'YYYY-MM-DD HH:mm:ss[Z]').isValid()) {
    const dateTz = dayjs(d, 'YYYY-MM-DD hh:mm:ss[Z]').tz(z).local().toDate();
    return dayjs(dateTz).format(dateFormat);
  }
  // Keep a generic parser as a last resort for older callers that send loosely formatted values.
  const dateTz = dayjs(d).tz(z).local().toDate();
  return dayjs(dateTz).format(dateFormat);
};

export const convertToDate = function convertToDate(date: unknown, time: string | number, zone?: string | null, seconds?: string | number, isUserDateFormat?: boolean): string | null | undefined {
  const dateFormat = isUserDateFormat && defaultDateFormat ? defaultDateFormat : 'YYYY-MM-DD';
  if (!date) {
    return undefined;
  }
  if (!zone) {
    zone = defaultTimeZone;
  }
  const z = zone as string;
  const d = date as ConfigType;
  let newDate: string | number | null = null;
  if (dayjs.isDayjs(date)) {
    newDate = date.tz(z).format(dateFormat);
  } else if (date instanceof Date) {
    newDate = dayjs(date).format(dateFormat);
  } else if (dayjs(d, dateFormat, true).isValid()) {
    newDate = date as string | number;
  } else if (dayjs(d, 'x', true).isValid()) {
    newDate = dayjs(d, 'x', true).format(dateFormat);
  } else {
    // Reuse the timezone-aware parser first, then fall back to raw dayjs parsing for legacy inputs.
    let tempDayjs = getDayjsDate(date);
    if (!tempDayjs || !tempDayjs.isValid()) {
      tempDayjs = dayjs(d);
    }
    newDate = tempDayjs.format(dateFormat);
  }
  if (newDate) {
    return `${newDate} ${time}:${seconds || '00'} ${dayjs(newDate, dateFormat).tz(z).format('Z')}`;
  }
  return null;
};
